import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { Download, LogOut, RefreshCw, Lock, Pencil } from "lucide-react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import CircleEditor from "@/components/CircleEditor";

const COLLECTIONS = [
  { key: "quiz_submissions", label: "Quiz captures",
    cols: ["created_at", "result_element", "name", "email", "email_attached_at"] },
  { key: "newsletter", label: "Newsletter",
    cols: ["created_at", "email", "name"] },
  { key: "contacts", label: "Contact form",
    cols: ["created_at", "name", "email", "phone", "subject", "message"] },
  { key: "room_rentals", label: "Room rentals",
    cols: ["created_at", "name", "email", "phone", "rental_type", "preferred_dates", "duration", "profession", "message"] },
  { key: "checkins", label: "Body check-ins",
    cols: ["created_at", "mood"] },
  { key: "reset_signups", label: "7-Day Reset signups",
    cols: ["created_at", "email", "name"] },
];

const CIRCLE_TAB = "__circle_editor__";

const TOKEN_KEY = "sano_admin_token";

function fmtCell(col, val) {
  if (val === null || val === undefined || val === "") return "—";
  if (col.endsWith("_at") || col === "created_at") {
    const d = new Date(val);
    if (!isNaN(d)) return d.toLocaleString("en-US", { dateStyle: "medium", timeStyle: "short" });
  }
  if (Array.isArray(val)) return val.join(", ");
  return String(val);
}

function toCSV(rows, cols) {
  const escape = (v) => {
    const s = v === null || v === undefined ? "" : Array.isArray(v) ? v.join("; ") : String(v);
    return `"${s.replace(/"/g, '""')}"`;
  };
  const header = cols.join(",");
  const body = rows.map((r) => cols.map((c) => escape(r[c])).join(",")).join("\n");
  return header + "\n" + body;
}

function download(filename, text) {
  const blob = new Blob([text], { type: "text/csv;charset=utf-8" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url; a.download = filename;
  document.body.appendChild(a); a.click();
  setTimeout(() => { URL.revokeObjectURL(url); a.remove(); }, 0);
}

export default function AdminPage() {
  const [token, setToken] = useState(() => localStorage.getItem(TOKEN_KEY) || "");
  const [authed, setAuthed] = useState(false);
  const [active, setActive] = useState(COLLECTIONS[0].key);
  const [data, setData] = useState({}); // key -> {items, count}
  const [loading, setLoading] = useState(false);
  const [query, setQuery] = useState("");

  const verify = async (t) => {
    setLoading(true);
    try {
      await api.get(`/admin/export/newsletter`, { params: { token: t } });
      localStorage.setItem(TOKEN_KEY, t);
      setAuthed(true);
      toast.success("Welcome back.");
    } catch {
      toast.error("Invalid admin token.");
      setAuthed(false);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (token) verify(token);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadCollection = async (key) => {
    setLoading(true);
    try {
      const { data: resp } = await api.get(`/admin/export/${key}`, { params: { token } });
      setData((d) => ({ ...d, [key]: resp }));
    } catch {
      toast.error("Couldn't load. Token may be invalid.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (authed && active !== CIRCLE_TAB && !data[active]) loadCollection(active);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authed, active]);

  const logout = () => {
    localStorage.removeItem(TOKEN_KEY);
    setToken(""); setAuthed(false); setData({});
  };

  if (!authed) {
    return (
      <main className="min-h-[80vh] flex items-center justify-center px-6" data-testid="admin-login-page">
        <form
          onSubmit={(e) => { e.preventDefault(); if (token) verify(token); }}
          className="w-full max-w-md p-10 border"
          style={{ background: "var(--sano-bg)", borderColor: "var(--sano-border)" }}
          data-testid="admin-login-form"
        >
          <div className="flex items-center gap-2 eyebrow mb-4">
            <Lock size={14} strokeWidth={1.5} /> Sano · Admin
          </div>
          <h1 className="font-serif-display text-4xl leading-tight mb-2">
            Welcome back.
          </h1>
          <p className="text-sm leading-relaxed mb-6" style={{ color: "var(--sano-ink-2)" }}>
            Enter your admin token to see incoming leads from the website.
          </p>
          <input
            type="password"
            placeholder="Admin token"
            value={token}
            onChange={(e) => setToken(e.target.value)}
            className="w-full px-4 py-3 border bg-[#F9F6F0] focus:outline-none focus:ring-2 focus:ring-[#3A5A40] text-sm mb-4"
            style={{ borderColor: "var(--sano-border)" }}
            data-testid="admin-token-input"
            autoFocus
          />
          <button
            type="submit"
            disabled={loading || !token}
            className="w-full px-7 py-3.5 rounded-full text-sm font-medium text-[#F9F6F0] disabled:opacity-50"
            style={{ background: "var(--sano-olive)" }}
            data-testid="admin-login-submit"
          >
            {loading ? "Checking…" : "Enter"}
          </button>
          <div className="mt-6 text-xs" style={{ color: "var(--sano-ink-2)" }}>
            Your token lives in the backend `.env` as `ADMIN_EXPORT_TOKEN`.
          </div>
          <div className="mt-4 text-center">
            <Link to="/" className="text-xs border-b border-current pb-0.5">← Back to site</Link>
          </div>
        </form>
      </main>
    );
  }

  const current = data[active];
  const activeCol = COLLECTIONS.find((c) => c.key === active);
  const cols = activeCol ? activeCol.cols : [];
  const items = current?.items || [];
  const q = query.trim().toLowerCase();
  const filtered = q
    ? items.filter((it) => JSON.stringify(it).toLowerCase().includes(q))
    : items;

  return (
    <main className="min-h-[80vh]" data-testid="admin-page">
      <section className="py-12 lg:py-16">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="flex items-end justify-between flex-wrap gap-4 mb-10">
            <div>
              <div className="eyebrow mb-3">Sano · Admin</div>
              <h1 className="font-serif-display text-4xl sm:text-5xl leading-tight">Your incoming leads.</h1>
            </div>
            <div className="flex items-center gap-3">
              <Link to="/" className="text-sm border-b border-current pb-0.5" data-testid="admin-back-home">
                ← Back to site
              </Link>
              <button
                onClick={logout}
                className="inline-flex items-center gap-1.5 text-sm border-b border-current pb-0.5"
                data-testid="admin-logout"
              >
                <LogOut size={13} strokeWidth={1.5} /> Sign out
              </button>
            </div>
          </div>

          {/* Tabs */}
          <div className="flex flex-wrap gap-2 mb-6">
            {COLLECTIONS.map((c) => (
              <button
                key={c.key}
                onClick={() => setActive(c.key)}
                className="px-5 py-2 rounded-full text-sm border transition-all"
                style={{
                  background: active === c.key ? "var(--sano-olive)" : "var(--sano-bg)",
                  color: active === c.key ? "var(--sano-bg)" : "var(--sano-ink)",
                  borderColor: active === c.key ? "var(--sano-olive)" : "var(--sano-border)",
                }}
                data-testid={`admin-tab-${c.key}`}
              >
                {c.label}
                {data[c.key] ? (
                  <span
                    className="ml-2 px-1.5 py-0.5 rounded-full text-[10px]"
                    style={{
                      background: active === c.key ? "rgba(249,246,240,0.18)" : "var(--sano-bg-3)",
                    }}
                  >
                    {data[c.key].count}
                  </span>
                ) : null}
              </button>
            ))}
            <button
              onClick={() => setActive(CIRCLE_TAB)}
              className="px-5 py-2 rounded-full text-sm border transition-all inline-flex items-center gap-1.5"
              style={{
                background: active === CIRCLE_TAB ? "var(--sano-olive)" : "var(--sano-bg)",
                color: active === CIRCLE_TAB ? "var(--sano-bg)" : "var(--sano-ink)",
                borderColor: active === CIRCLE_TAB ? "var(--sano-olive)" : "var(--sano-border)",
              }}
              data-testid="admin-tab-circle-editor"
            >
              <Pencil size={12} strokeWidth={1.5} /> Edit Sano Circle
            </button>
          </div>

          {active === CIRCLE_TAB ? (
            <CircleEditor token={token} />
          ) : (
          <>
          {/* Toolbar */}
          <div className="flex flex-wrap items-center gap-3 mb-4">
            <input
              type="search"
              placeholder="Search rows…"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              className="flex-1 min-w-[200px] max-w-md px-4 py-2.5 border bg-[#F9F6F0] focus:outline-none focus:ring-2 focus:ring-[#3A5A40] text-sm rounded-full"
              style={{ borderColor: "var(--sano-border)" }}
              data-testid="admin-search"
            />
            <button
              onClick={() => loadCollection(active)}
              className="inline-flex items-center gap-2 px-4 py-2.5 text-sm border rounded-full hover:bg-[#F0EBE1]"
              style={{ borderColor: "var(--sano-border)" }}
              data-testid="admin-refresh"
            >
              <RefreshCw size={14} strokeWidth={1.5} className={loading ? "animate-spin" : ""} />
              Refresh
            </button>
            <button
              onClick={() => {
                if (!filtered.length) { toast.error("Nothing to export."); return; }
                const csv = toCSV(filtered, cols);
                const stamp = new Date().toISOString().slice(0, 10);
                download(`sano_${active}_${stamp}.csv`, csv);
              }}
              className="inline-flex items-center gap-2 px-4 py-2.5 text-sm rounded-full text-[#F9F6F0]"
              style={{ background: "var(--sano-olive)" }}
              data-testid="admin-download-csv"
            >
              <Download size={14} strokeWidth={1.5} />
              Download CSV
            </button>
          </div>

          {/* Table */}
          <div
            className="border overflow-x-auto"
            style={{ borderColor: "var(--sano-border)", background: "var(--sano-bg)" }}
            data-testid="admin-table-wrap"
          >
            <table className="min-w-full text-sm" data-testid="admin-table">
              <thead style={{ background: "var(--sano-bg-2)" }}>
                <tr>
                  {cols.map((c) => (
                    <th
                      key={c}
                      className="text-left px-4 py-3 eyebrow whitespace-nowrap"
                      style={{ borderBottom: "1px solid var(--sano-border)" }}
                    >
                      {c.replace(/_/g, " ")}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {loading && !current && (
                  <tr><td colSpan={cols.length} className="px-4 py-10 text-center" style={{ color: "var(--sano-ink-2)" }}>Loading…</td></tr>
                )}
                {!loading && filtered.length === 0 && (
                  <tr>
                    <td colSpan={cols.length} className="px-4 py-12 text-center" style={{ color: "var(--sano-ink-2)" }}>
                      {q ? "No rows match your search." : "No entries yet."}
                    </td>
                  </tr>
                )}
                {filtered.map((row, i) => (
                  <tr key={row.id || i} data-testid={`admin-row-${i}`}>
                    {cols.map((c) => (
                      <td
                        key={c}
                        className="px-4 py-3 align-top max-w-xs"
                        style={{ borderBottom: "1px solid var(--sano-border)" }}
                      >
                        <div className="whitespace-pre-wrap break-words">{fmtCell(c, row[c])}</div>
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="mt-4 text-xs" style={{ color: "var(--sano-ink-2)" }}>
            {filtered.length} of {items.length} rows · {COLLECTIONS.find((c) => c.key === active)?.label}
          </div>
          </>
          )}
        </div>
      </section>
    </main>
  );
}
